import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { unifiedOptimization, OptimizationResult } from "@/lib/autoPatternOptimization";
// Advanced optimization import (commented out for now, can be re-enabled in the future)
// import { advancedOptimization } from "@/lib/advancedOptimization";
import { CartonInputsTab } from './components/tabs/CartonInputsTab';
import { PalletInputsTab } from './components/tabs/PalletInputsTab';
import { ContainerInputsTab } from './components/tabs/ContainerInputsTab';
import { OptimizationSettingsTab } from './components/tabs/OptimizationSettingsTab';
import { ResultsTab } from './components/tabs/ResultsTab';
import { visualConfig } from './config/visualConfig';
import './index.css';

function App() {
  const [showVisualization, setShowVisualization] = useState(false);
  const [optimizationResult, setOptimizationResult] = useState<OptimizationResult | null>(null);
  const [activeTab, setActiveTab] = useState("carton-inputs");

  // State for Container Inputs
  const [containerLength, setContainerLength] = useState(1219.2);
  const [containerWidth, setContainerWidth] = useState(243.8);
  const [containerHeight, setContainerHeight] = useState(259.1);
  const [containerWeightCapacity, setContainerWeightCapacity] = useState(26000);

  // State for Pallet Inputs
  const [palletLength, setPalletLength] = useState(120);
  const [palletWidth, setPalletWidth] = useState(80);
  const [palletHeight, setPalletHeight] = useState(14.5);

  const [maxStackHeight, setMaxStackHeight] = useState(200);
  const [maxStackWeight, setMaxStackWeight] = useState(1000);

  // State for Carton Inputs
  const [cartonLength, setCartonLength] = useState(50);
  const [cartonWidth, setCartonWidth] = useState(30);
  const [cartonHeight, setCartonHeight] = useState(25);
  const [cartonWeight, setCartonWeight] = useState(15);
  const [cartonQuantity, setCartonQuantity] = useState(200);

  // State for Optimization Settings
  const [rotationEnabled, setRotationEnabled] = useState(true);
  const [usePallets, setUsePallets] = useState(true);
  const [thisSideUp, setThisSideUp] = useState(false);
  const [loadBearingCapacity, setLoadBearingCapacity] = useState(false);
  const [stackingPattern, setStackingPattern] = useState("auto");
  
  // Advanced Algorithm Toggle (disabled for now, can be re-enabled in the future)
  // const [useAdvancedAlgorithm] = useState(false);

  // State for Unit System
  const [unitSystem] = useState<'metric' | 'imperial'>('metric');

  const handleOptimize = () => {
    // Comprehensive input validation before optimization
    const validationErrors: string[] = [];
    
    // Validate carton dimensions
    if (cartonLength <= 0) {
      validationErrors.push("Carton length must be greater than zero");
    }
    if (cartonWidth <= 0) {
      validationErrors.push("Carton width must be greater than zero");
    }
    if (cartonHeight <= 0) {
      validationErrors.push("Carton height must be greater than zero");
    }
    if (cartonWeight <= 0) {
      validationErrors.push("Carton weight must be greater than zero");
    }
    if (cartonQuantity <= 0) {
      validationErrors.push("Carton quantity must be at least 1");
    }
    
    // Validate container dimensions
    if (containerLength <= 0) {
      validationErrors.push("Container length must be greater than zero");
    }
    if (containerWidth <= 0) {
      validationErrors.push("Container width must be greater than zero");
    }
    if (containerHeight <= 0) {
      validationErrors.push("Container height must be greater than zero");
    }
    if (containerWeightCapacity <= 0) {
      validationErrors.push("Container weight capacity must be greater than zero");
    }
    
    // Validate pallet dimensions if using pallets
    if (usePallets) {
      if (palletLength <= 0) {
        validationErrors.push("Pallet length must be greater than zero");
      }
      if (palletWidth <= 0) {
        validationErrors.push("Pallet width must be greater than zero");
      }
      if (palletHeight <= 0) {
        validationErrors.push("Pallet height must be greater than zero");
      }
      if (maxStackWeight <= 0) {
        validationErrors.push("Maximum stack weight must be greater than zero");
      }
    }
    
    // Validate stack height
    if (maxStackHeight <= 0) {
      validationErrors.push("Maximum stack height must be greater than zero");
    }
    
    // Validate logical constraints
    if (usePallets) {
      if (cartonLength > palletLength) {
        validationErrors.push("Carton length cannot be greater than pallet length");
      }
      if (cartonWidth > palletWidth) {
        validationErrors.push("Carton width cannot be greater than pallet width");
      }
      if (palletLength > containerLength) {
        validationErrors.push("Pallet length cannot be greater than container length");
      }
      if (palletWidth > containerWidth) {
        validationErrors.push("Pallet width cannot be greater than container width");
      }
      if (maxStackHeight > containerHeight) {
        validationErrors.push("Maximum stack height cannot be greater than container height");
      }
    } else {
      if (cartonLength > containerLength) {
        validationErrors.push("Carton length cannot be greater than container length");
      }
      if (cartonWidth > containerWidth) {
        validationErrors.push("Carton width cannot be greater than container width");
      }
      if (cartonHeight > containerHeight) {
        validationErrors.push("Carton height cannot be greater than container height");
      }
    }
    
    // Check for NaN values
    const allValues = [
      { name: "carton length", value: cartonLength },
      { name: "carton width", value: cartonWidth },
      { name: "carton height", value: cartonHeight },
      { name: "carton weight", value: cartonWeight },
      { name: "carton quantity", value: cartonQuantity },
      { name: "container length", value: containerLength },
      { name: "container width", value: containerWidth },
      { name: "container height", value: containerHeight },
      { name: "container weight capacity", value: containerWeightCapacity },
      { name: "maximum stack height", value: maxStackHeight }
    ];
    
    if (usePallets) {
      allValues.push(
        { name: "pallet length", value: palletLength },
        { name: "pallet width", value: palletWidth },
        { name: "pallet height", value: palletHeight },
        { name: "maximum stack weight", value: maxStackWeight }
      );
    }
    
    allValues.forEach(({ name, value }) => {
      if (isNaN(value) || !isFinite(value)) {
        validationErrors.push(`${name.charAt(0).toUpperCase() + name.slice(1)} must be a valid number`);
      }
    });
    
    // If there are validation errors, show them and stop optimization
    if (validationErrors.length > 0) {
      const errorResult = {
        packedContainers: [],
        utilization: 0,
        spaceUtilization: 0,
        weightUtilization: 0,
        totalCartonsPacked: 0,
        remainingCartons: 0,
        totalPalletsUsed: 0,
        patternComparison: { column: 0, interlock: 0, brick: 0 },
        selectedPattern: 'auto',
        bestOrientation: '',
        error: `Please fix the following input errors:\n\n${validationErrors.map((error, index) => `${index + 1}. ${error}`).join('\n')}`
      };
      
      console.log("=== VALIDATION ERRORS ===");
      console.log("Errors found:", validationErrors);
      
      setOptimizationResult(errorResult);
      setShowVisualization(false);
      setActiveTab("results");
      return;
    }
    
    const carton = {
      length: cartonLength,
      width: cartonWidth,
      height: cartonHeight,
      weight: cartonWeight,
      quantity: cartonQuantity,
    };

    console.log("=== OPTIMIZATION START ===");
    console.log("Input carton object:", carton);

    const constraints = {
      maxStackHeight: maxStackHeight,
      allowRotationOnBase: rotationEnabled,
      allowVerticalRotation: !thisSideUp, // Enable vertical rotation unless thisSideUp is checked
      stackingPattern: stackingPattern,
    };

    const container = {
      length: containerLength,
      width: containerWidth,
      height: containerHeight,
      maxWeight: containerWeightCapacity,
    };

    const pallet = {
      length: palletLength,
      width: palletWidth,
      height: palletHeight,
      maxWeight: maxStackWeight,
    };

    // Use standard optimization algorithm
    console.log("=== USING STANDARD OPTIMIZATION ALGORITHM ===");
    
    const result = unifiedOptimization(
      carton,
      constraints,
      usePallets,
      true,
      container,
      pallet,
      thisSideUp
    );

    console.log("=== OPTIMIZATION RESULT ===");
    console.log("Result totalCartonsPacked:", result.totalCartonsPacked);
    console.log("Result remainingCartons:", result.remainingCartons);
    console.log("Full result:", result);

    setOptimizationResult(result);
    setShowVisualization(true);
    setActiveTab("results");
  };

  const resetResults = () => {
    setOptimizationResult(null)
    setShowVisualization(false)
  }

  const setCartonLengthAndReset = (value: number) => {
    setCartonLength(value)
    resetResults()
  }
  const setCartonWidthAndReset = (value: number) => {
    setCartonWidth(value)
    resetResults()
  }
  const setCartonHeightAndReset = (value: number) => {
    setCartonHeight(value)
    resetResults()
  }
  const setCartonWeightAndReset = (value: number) => {
    setCartonWeight(value)
    resetResults()
  }
  const setCartonQuantityAndReset = (value: number) => {
    setCartonQuantity(value)
    resetResults()
  }
  const setPalletLengthAndReset = (value: number) => {
    setPalletLength(value)
    resetResults()
  }
  const setPalletWidthAndReset = (value: number) => {
    setPalletWidth(value)
    resetResults()
  }
  const setPalletHeightAndReset = (value: number) => {
    setPalletHeight(value)
    resetResults()
  }

  const setMaxStackHeightAndReset = (value: number) => {
    setMaxStackHeight(value)
    resetResults()
  }
  const setMaxStackWeightAndReset = (value: number) => {
    setMaxStackWeight(value)
    resetResults()
  }
  const setContainerLengthAndReset = (value: number) => {
    setContainerLength(value)
    resetResults()
  }
  const setContainerWidthAndReset = (value: number) => {
    setContainerWidth(value)
    resetResults()
  }
  const setContainerHeightAndReset = (value: number) => {
    setContainerHeight(value)
    resetResults()
  }
  const setContainerWeightCapacityAndReset = (value: number) => {
    setContainerWeightCapacity(value)
    resetResults()
  }
  const setRotationEnabledAndReset = (value: boolean) => {
    setRotationEnabled(value)
    resetResults()
  }
  const setThisSideUpAndReset = (value: boolean) => {
    setThisSideUp(value)
    resetResults()
  }
  const setLoadBearingCapacityAndReset = (value: boolean) => {
    setLoadBearingCapacity(value)
    resetResults()
  }
  const setStackingPatternAndReset = (value: string) => {
    setStackingPattern(value)
    resetResults()
  }

  // Navigation functions
  const tabs = ["carton-inputs", "pallet-inputs", "container-inputs", "optimization-settings", "results"]
  const currentTabIndex = tabs.indexOf(activeTab)
  
  const goToPreviousTab = () => {
    if (currentTabIndex > 0) {
      setActiveTab(tabs[currentTabIndex - 1])
    }
  }
  
  const goToNextTab = () => {
    if (currentTabIndex < tabs.length - 1) {
      setActiveTab(tabs[currentTabIndex + 1])
    }
  }

  return (
    <div className={`flex flex-col items-center min-h-screen ${visualConfig.layout.container.background} ${visualConfig.layout.container.padding}`}>
      <div className={`w-full ${visualConfig.layout.container.maxWidth}`}>
        {/* Logo and Title Section - Commented Out
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1 sm:gap-2 md:gap-4 mb-2 sm:mb-3 md:mb-4 lg:mb-6">
          <img 
            src="/logo.png" 
            alt="Pallet Calculator Logo" 
            className={`${visualConfig.components.logo.size} ${visualConfig.components.logo.style}`}
          />
          <div className="flex-1">
            <h1 className={visualConfig.typography.mainTitle}>
              Pallet Calculator
            </h1>
            <p className={visualConfig.typography.subtitle}>
              Optimize your loading efficiency
            </p>
          </div>
        </div>
        */}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className={`${visualConfig.components.tabs.container} ${visualConfig.colors.navigation.background}`}>
            <TabsTrigger value="carton-inputs" className={`${visualConfig.colors.navigation.inactive} ${visualConfig.colors.navigation.active} data-[state=active]:shadow-sm ${visualConfig.components.tabs.trigger}`}>Carton</TabsTrigger>
            <TabsTrigger value="pallet-inputs" className={`${visualConfig.colors.navigation.inactive} ${visualConfig.colors.navigation.active} data-[state=active]:shadow-sm ${visualConfig.components.tabs.trigger}`}>Pallet</TabsTrigger>
            <TabsTrigger value="container-inputs" className={`${visualConfig.colors.navigation.inactive} ${visualConfig.colors.navigation.active} data-[state=active]:shadow-sm ${visualConfig.components.tabs.trigger}`}>Container</TabsTrigger>
            <TabsTrigger value="optimization-settings" className={`${visualConfig.colors.navigation.inactive} ${visualConfig.colors.navigation.active} data-[state=active]:shadow-sm ${visualConfig.components.tabs.trigger}`}>Settings</TabsTrigger>
            <TabsTrigger value="results" className={`${visualConfig.colors.navigation.inactive} ${visualConfig.colors.navigation.active} data-[state=active]:shadow-sm ${visualConfig.components.tabs.trigger}`}>Results</TabsTrigger>
          </TabsList>

          <TabsContent value="carton-inputs">
            <CartonInputsTab
              cartonLength={cartonLength}
              cartonWidth={cartonWidth}
              cartonHeight={cartonHeight}
              cartonWeight={cartonWeight}
              cartonQuantity={cartonQuantity}
              unitSystem={unitSystem}
              setCartonLengthAndReset={setCartonLengthAndReset}
              setCartonWidthAndReset={setCartonWidthAndReset}
              setCartonHeightAndReset={setCartonHeightAndReset}
              setCartonWeightAndReset={setCartonWeightAndReset}
              setCartonQuantityAndReset={setCartonQuantityAndReset}
              goToNextTab={goToNextTab}
            />
          </TabsContent>

          <TabsContent value="pallet-inputs">
            <PalletInputsTab
              usePallets={usePallets}
              setUsePallets={setUsePallets}
              palletLength={palletLength}
              palletWidth={palletWidth}
              palletHeight={palletHeight}
              maxStackHeight={maxStackHeight}
              maxStackWeight={maxStackWeight}
              unitSystem={unitSystem}
              setPalletLengthAndReset={setPalletLengthAndReset}
              setPalletWidthAndReset={setPalletWidthAndReset}
              setPalletHeightAndReset={setPalletHeightAndReset}
              setMaxStackHeightAndReset={setMaxStackHeightAndReset}
              setMaxStackWeightAndReset={setMaxStackWeightAndReset}
              goToPreviousTab={goToPreviousTab}
              goToNextTab={goToNextTab}
            />
          </TabsContent>

          <TabsContent value="container-inputs">
            <ContainerInputsTab
              containerLength={containerLength}
              containerWidth={containerWidth}
              containerHeight={containerHeight}
              containerWeightCapacity={containerWeightCapacity}
              unitSystem={unitSystem}
              setContainerLengthAndReset={setContainerLengthAndReset}
              setContainerWidthAndReset={setContainerWidthAndReset}
              setContainerHeightAndReset={setContainerHeightAndReset}
              setContainerWeightCapacityAndReset={setContainerWeightCapacityAndReset}
              goToPreviousTab={goToPreviousTab}
              goToNextTab={goToNextTab}
            />
          </TabsContent>

          <TabsContent value="optimization-settings">
            <OptimizationSettingsTab
              stackingPattern={stackingPattern}
              rotationEnabled={rotationEnabled}
              thisSideUp={thisSideUp}
              loadBearingCapacity={loadBearingCapacity}
              usePallets={usePallets}
              setStackingPatternAndReset={setStackingPatternAndReset}
              setRotationEnabledAndReset={setRotationEnabledAndReset}
              setThisSideUpAndReset={setThisSideUpAndReset}
              setLoadBearingCapacityAndReset={setLoadBearingCapacityAndReset}
              handleOptimize={handleOptimize}
              goToPreviousTab={goToPreviousTab}
            />
          </TabsContent>

          <TabsContent value="results">
            <ResultsTab
              optimizationResult={optimizationResult}
              showVisualization={showVisualization}
              cartonQuantity={cartonQuantity}
              usePallets={usePallets}
              cartonLength={cartonLength}
              cartonWidth={cartonWidth}
              cartonHeight={cartonHeight}
              containerLength={containerLength}
              containerWidth={containerWidth}
              containerHeight={containerHeight}
              containerWeightCapacity={containerWeightCapacity}
              goToPreviousTab={goToPreviousTab}
            />
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Build Date Footer */}
      <div className={visualConfig.components.buildInfo}>
        Build: {process.env.REACT_APP_BUILD_TIME || new Date().toISOString()}
      </div>
    </div>
  );
}

export default App; 